import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { cast, types } from 'mobx-state-tree';
import { toJS } from 'mobx';
import { api } from 'src/api';
import { createAsync, createSilentAsync } from 'src/utils/asyncModel';
function readServerErrorPayload(err) {
  var _response;
  const resp = err === null || err === void 0 || (_response = err.response) === null || _response === void 0 ? void 0 : _response.data;
  return resp && typeof resp === 'object' ? resp : {};
}

/** The server's own wording, ungated: these surfaces are staff-only and render it themselves. */
function readServerErrorMessage(err) {
  var _data$Error, _envelope$GeneralErro;
  const data = readServerErrorPayload(err);
  const envelope = (_data$Error = data.Error) !== null && _data$Error !== void 0 ? _data$Error : data.error;
  const candidates = [typeof envelope === 'object' ? (_envelope$GeneralErro = envelope === null || envelope === void 0 ? void 0 : envelope.GeneralErrorMessage) !== null && _envelope$GeneralErro !== void 0 ? _envelope$GeneralErro : envelope === null || envelope === void 0 ? void 0 : envelope.generalErrorMessage : envelope, data.message, err === null || err === void 0 ? void 0 : err.message];
  const found = candidates.find(c => typeof c === 'string' && c.trim().length > 0);
  return typeof found === 'string' ? found.trim() : null;
}

/** API exposes minimumPrice on the price; older payloads nested it under tier/table. */
function resolveMinimumPrice(p) {
  var _p$tierTableResponse;
  const top = p.minimumPrice;
  if (typeof top === 'number' && !Number.isNaN(top)) return top;
  if (top === null) return null;
  const tierRaw = (_p$tierTableResponse = p.tierTableResponse) !== null && _p$tierTableResponse !== void 0 ? _p$tierTableResponse : p.priceTableResponse;
  const nested = tierRaw && typeof tierRaw === 'object' && tierRaw !== null ? tierRaw.minimumPrice : undefined;
  if (typeof nested === 'number' && !Number.isNaN(nested)) return nested;
  return null;
}

/**
 * MeterEvent reaches us by name, but a price row holding a value the enum does not define comes back
 * as the raw number instead. The model asks for a string, so one such price rejected the whole list
 * and left the Subscriptions tab empty. Kept as it arrived rather than guessed at.
 */
function normalizeMeterEvent(raw) {
  return raw == null ? '' : String(raw).trim();
}
function normalizeTierTableSnapshotForPriceList(tierRaw) {
  if (tierRaw == null || typeof tierRaw !== 'object') return null;
  const o = tierRaw;
  return {
    tierPrice: Array.isArray(o.tierPrice) ? o.tierPrice : []
  };
}
const getPriceDashboardAsync = createAsync(() => {
  return async function getPriceDashboard(options) {
    var _resp$response;
    const resp = await api.Stripe.getPriceDashboard(options);
    const data = Array.isArray(resp) ? resp : (_resp$response = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response !== void 0 ? _resp$response : [];
    this.setPriceDashboard(data);
    return resp;
  };
});
const getSubscriptionTemplatesAsync = createAsync(() => {
  return async function getSubscriptionTemplates(options) {
    var _resp$response2;
    const resp = await api.Stripe.getSubscriptionTemplates(options);
    const list = (_resp$response2 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response2 !== void 0 ? _resp$response2 : [];
    this.setSubscriptionTemplates(Array.isArray(list) ? list : []);
    return resp;
  };
});
const createPriceAsync = createAsync(body => {
  return async function createPrice(options) {
    const resp = await api.Stripe.createPrice(body, options);
    // API may return the new price id (string) in response, not a full list — only merge when it's an array.
    const list = resp === null || resp === void 0 ? void 0 : resp.response;
    if (Array.isArray(list)) {
      this.setPrices(list);
    }
    return resp;
  };
});
const getPricesAsync = createAsync(() => {
  return async function getPrices(options) {
    var _resp$response3;
    const resp = await api.Stripe.getPrices(options);
    const list = (_resp$response3 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response3 !== void 0 ? _resp$response3 : [];
    this.setPrices(Array.isArray(list) ? list : []);
    return resp;
  };
});
const detachPriceFromSubscriptionAsync = createAsync(body => {
  return async function detachPriceFromSubscription(options) {
    const resp = await api.Stripe.detachPriceFromSubscription(body, options);
    return resp;
  };
});
const deletePriceAsync = createAsync(body => {
  return async function deletePrice(options) {
    const resp = await api.Stripe.deletePrice(body, options);
    return resp;
  };
});
const getCouponsAsync = createAsync(() => {
  return async function getCoupons(options) {
    var _resp$response4;
    const resp = await api.Stripe.getCoupons(options);
    const list = (_resp$response4 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response4 !== void 0 ? _resp$response4 : [];
    this.setCoupons(Array.isArray(list) ? list : []);
    return resp;
  };
});
const createCouponAsync = createAsync(body => {
  return async function createCoupon(options) {
    const resp = await api.Stripe.createCoupon(body, options);
    return resp;
  };
});
const updateCouponAsync = createAsync(body => {
  return async function updateCoupon(options) {
    const resp = await api.Stripe.updateCoupon(body, options);
    return resp;
  };
});
const deleteCouponAsync = createAsync(body => {
  return async function deleteCoupon(options) {
    const resp = await api.Stripe.deleteCoupon(body, options);
    return resp;
  };
});
const getCompanyCouponRedemptionsAsync = createAsync(body => {
  return async function getCompanyCouponRedemptions(options) {
    var _resp$response5;
    const resp = await api.Stripe.getCompanyCouponRedemptions(body, options);
    this.setCompanyRedemptions((_resp$response5 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response5 !== void 0 ? _resp$response5 : []);
    return resp;
  };
});
const getCouponRedemptionsAsync = createAsync(body => {
  return async function getCouponRedemptions(options) {
    var _resp$response6;
    const resp = await api.Stripe.getCouponRedemptions(body, options);
    this.setCouponRedemptions((_resp$response6 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response6 !== void 0 ? _resp$response6 : []);
    return resp;
  };
});
const applyCouponAsync = createAsync(body => {
  return async function applyCoupon(options) {
    const resp = await api.Stripe.applyCoupon(body, options);
    return resp;
  };
});
const getOwnCompanyCouponRedemptionsAsync = createAsync(() => {
  return async function getOwnCompanyCouponRedemptions(options) {
    var _resp$response7;
    const resp = await api.Stripe.getOwnCompanyCouponRedemptions(options);
    this.setCompanyRedemptions((_resp$response7 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response7 !== void 0 ? _resp$response7 : []);
    return resp;
  };
});
const redeemCouponAsync = createAsync(body => {
  return async function redeemCoupon(options) {
    const resp = await api.Stripe.redeemCoupon(body, options);
    return resp;
  };
});
const revokeCouponRedemptionAsync = createAsync(body => {
  return async function revokeCouponRedemption(options) {
    const resp = await api.Stripe.revokeCouponRedemption(body, options);
    return resp;
  };
});
const getAllProductsAsync = createAsync(() => {
  return async function getAllProducts(options) {
    var _resp$response8;
    const resp = await api.Stripe.getAllProducts(options);
    this.setProductList((_resp$response8 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response8 !== void 0 ? _resp$response8 : []);
    return resp;
  };
});

// const getSubscriptionDashboardAsync = createAsync(() => {
//   return async function getSubscriptionDashboard(this, options) {
//     const resp = await api.Stripe.getSubscriptionDashboard(options);

//     this.setSubscriptionDashboard(resp?.response ?? []);

//     return resp;
//   };
// });

const createSubscriptionAsync = createAsync(body => {
  return async function createSubscription(options) {
    const resp = await api.Stripe.createSubscription(body, options);
    return resp;
  };
});
const updatePriceAsync = createAsync(body => {
  return async function updatePrice(options) {
    const resp = await api.Stripe.updatePrice(body, options);
    return resp;
  };
});
function parseSubscriptionsPagedResponse(resp) {
  const raw = resp === null || resp === void 0 ? void 0 : resp.response;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && Array.isArray(raw.items)) {
    return raw.items;
  }
  return [];
}
const getSubscriptionsPagedAsync = createAsync(body => {
  return async function getSubscriptionsPaged(options) {
    const resp = await api.Stripe.getSubscriptionsPaged(body, options);
    const items = parseSubscriptionsPagedResponse(resp);
    this.setSubscriptionsPaged(items, items.length);
    return resp;
  };
});

// Pages are asked for one after another, never at once: each concurrent request can wake its own cold
// Lambda, and parallel pages measured 6–17 s where a single request took about 2 s.
const getSubscriptionsLoadAllAsync = createAsync(body => {
  return async function getSubscriptionsLoadAll(options) {
    const bodyOpts = body;
    const pageSize = typeof (bodyOpts === null || bodyOpts === void 0 ? void 0 : bodyOpts.pageSize) === 'number' ? bodyOpts.pageSize : 25;
    const all = [];
    let pageNumber = 1;
    for (;;) {
      const resp = await api.Stripe.getSubscriptionsPaged({
        pageNumber,
        pageSize
      }, options);
      const items = parseSubscriptionsPagedResponse(resp);
      all.push(...items);
      if (items.length < pageSize) break;
      pageNumber += 1;
    }
    this.setSubscriptionsPaged(all, all.length);
    return {
      response: all
    };
  };
});
const updateSubscriptionAsync = createAsync(body => {
  return async function updateSubscription(options) {
    const resp = await api.Stripe.updateSubscription(body, options);
    return resp;
  };
});
const bulkUpdateSubscriptionsAsync = createAsync(body => {
  return async function bulkUpdateSubscriptions(options) {
    const resp = await api.Stripe.bulkUpdateSubscriptions(body, options);
    return resp;
  };
});
const getMeterAsync = createAsync(() => {
  return async function getMeter(options) {
    var _resp$response9;
    const resp = await api.Stripe.getMeter(options);
    this.setMeters((_resp$response9 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response9 !== void 0 ? _resp$response9 : []);
    return resp;
  };
});
const createProductAsync = createAsync(body => {
  return async function createProduct(options) {
    const resp = await api.Stripe.createProduct(body, options);
    return resp;
  };
});
const deleteProductAsync = createAsync(body => {
  return async function deleteProduct(options) {
    this.setProductDeleteErrorMessage(null);
    try {
      const resp = await api.Stripe.deleteProduct(body, options);
      return resp;
    } catch (err) {
      // A product with a live coupon (or price) attached cannot be deleted, and the reason names the
      // blocker. Keep the server's wording for the caller to render.
      this.setProductDeleteErrorMessage(readServerErrorMessage(err));
      throw err;
    }
  };
});
const updateProductAsync = createAsync(body => {
  return async function updateProduct(options) {
    const resp = await api.Stripe.updateProduct(body, options);
    return resp;
  };
});

// Silent: the plans add a chip and two fields, so a failed read leaves Pro Pricing as it was rather than raising a toast.
const getProductPlansAsync = createSilentAsync(() => {
  return async function getProductPlans(options) {
    var _resp$response0;
    const resp = await api.Stripe.getProductPlans(options);
    this.setProductPlans((_resp$response0 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response0 !== void 0 ? _resp$response0 : []);
    return resp;
  };
});
const setProductPlanAsync = createAsync(body => {
  return async function setProductPlan(options) {
    const resp = await api.Stripe.setProductPlan(body, options);

    // Kept at once, so reopening the product before the next read shows what was saved, not the plan before it.
    this.noteProductPlan(body);
    return resp;
  };
});
const bulkDeletePricesAsync = createAsync(body => {
  return async function bulkDeletePrices(options) {
    const resp = await api.Stripe.bulkDeletePrices(body, options);
    return resp;
  };
});
const getInvoicesAsync = createAsync(() => {
  return async function getInvoices(options) {
    const resp = await api.Stripe.getInvoices(options);
    return resp;
  };
});
const getSubscriptionCompanyAsync = createAsync(() => {
  return async function getSubscriptionCompany(options) {
    const resp = await api.Stripe.getSubscriptionCompany(undefined, options);
    if (resp !== null && resp !== void 0 && resp.response) this.setSubscriptionCompany(resp.response);
    return resp;
  };
});
const getSubscriptionCompanyForCompanyAsync = createAsync(body => {
  return async function getSubscriptionCompanyForCompany(options) {
    const resp = await api.Stripe.getSubscriptionCompany(body, options);
    return resp;
  };
});
const getUpcomingInvoicesAsync = createAsync(() => {
  return async function getUpcomingInvoices(options) {
    const resp = await api.Stripe.getUpcomingInvoices(options);
    return resp;
  };
});
const getScheduledChangeEstimateAsync = createSilentAsync(body => {
  return async function getScheduledChangeEstimate(options) {
    const resp = await api.Stripe.getScheduledChangeEstimate(body, options);
    return resp;
  };
});
const scheduleChangeAsync = createAsync(body => {
  return async function scheduleChange(options) {
    const resp = await api.Stripe.scheduleChange(body, options);
    return resp;
  };
});
const rescheduleChangeAsync = createAsync(body => {
  return async function rescheduleChange(options) {
    const resp = await api.Stripe.rescheduleChange(body, options);
    return resp;
  };
});
const removeScheduledChangeAsync = createAsync(body => {
  return async function removeScheduledChange(options) {
    const resp = await api.Stripe.removeScheduledChange(body, options);
    return resp;
  };
});
const StripePrice = types.model('StripePrice', {
  id: types.optional(types.string, ''),
  name: types.optional(types.string, '')
});
const StripePriceSubscriptionResponse = types.model('StripePriceSubscriptionResponse', {
  subscriptionId: types.optional(types.string, ''),
  companyId: types.optional(types.string, ''),
  companyName: types.optional(types.string, ''),
  accountHolderEmail: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  scope: types.optional(types.string, ''),
  startDate: types.optional(types.string, ''),
  propertiesCount: types.optional(types.number, 0),
  unitsCount: types.optional(types.number, 0)
});
const StripePriceResponse = types.model('StripePriceResponse', {
  priceId: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  productId: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  isDefault: types.optional(types.boolean, false),
  meterEvent: types.maybeNull(types.optional(types.string, '')),
  fixedPrice: types.maybeNull(types.optional(types.number, 0)),
  /** Minimum charge at price level (API); not inside tierTableResponse. */
  minimumPrice: types.optional(types.maybeNull(types.number), null),
  tierTableResponse: types.maybeNull(types.frozen()),
  subscriptionResponse: types.optional(types.array(StripePriceSubscriptionResponse), []),
  subscriptionCount: types.optional(types.number, 0)
});
const StripeProductWithPricesResponse = types.model('StripeProductWithPricesResponse', {
  productId: types.optional(types.string, ''),
  productName: types.optional(types.string, ''),
  priceResponse: types.optional(types.array(StripePriceResponse), [])
});

// Types for getSubscriptionDashboard response
const StripeSubscriptionDashboardPriceItem = types.model('StripeSubscriptionDashboardPriceItem', {
  id: types.optional(types.string, ''),
  priceType: types.optional(types.string, ''),
  meterEvent: types.optional(types.string, ''),
  fixedPrice: types.maybeNull(types.optional(types.number, 0)),
  /** Minimum charge at price level (API); not inside priceTableResponse. */
  minimumPrice: types.optional(types.maybeNull(types.number), null),
  priceName: types.optional(types.string, ''),
  productId: types.optional(types.string, ''),
  productName: types.optional(types.string, ''),
  priceTableId: types.optional(types.maybeNull(types.string), ''),
  priceTableResponse: types.optional(types.maybeNull(types.frozen()), [])
});
const StripeScheduledChange = types.model('StripeScheduledChange', {
  id: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  effectiveFrom: types.optional(types.maybeNull(types.string), null),
  effectiveUntil: types.optional(types.maybeNull(types.string), null),
  reason: types.optional(types.maybeNull(types.string), null),
  createdAt: types.optional(types.maybeNull(types.string), null),
  createdBy: types.optional(types.maybeNull(types.string), null),
  createdByFullname: types.optional(types.maybeNull(types.string), null)
});
const StripeSubscriptionDashboardResponse = types.model('StripeSubscriptionDashboardResponse', {
  id: types.optional(types.string, ''),
  companyId: types.optional(types.string, ''),
  companyName: types.optional(types.string, ''),
  companyHolderEmail: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  scope: types.optional(types.string, ''),
  propertiesCount: types.optional(types.number, 0),
  activeUnitsCount: types.optional(types.number, 0),
  billableUnitsCount: types.optional(types.number, 0),
  startDate: types.optional(types.maybeNull(types.string), ''),
  term: types.optional(types.maybeNull(types.string), ''),
  commitmentEndDate: types.optional(types.maybeNull(types.string), null),
  renewalDate: types.optional(types.maybeNull(types.string), ''),
  terminationDate: types.optional(types.maybeNull(types.string), null),
  trialDays: types.optional(types.maybeNull(types.number), null),
  trialName: types.optional(types.maybeNull(types.string), ''),
  /** Last day of the free trial, derived by the backend from startDate and trialDays. */
  trialEndDate: types.optional(types.maybeNull(types.string), null),
  prices: types.optional(types.array(StripeSubscriptionDashboardPriceItem), []),
  subscriptionPropertyIds: types.optional(types.array(types.string), []),
  scheduledChanges: types.optional(types.array(StripeScheduledChange), [])
});
const StripePriceTierItem = types.model('StripePriceTierItem', {
  unitQuantity: types.optional(types.number, 0),
  amount: types.optional(types.number, 0)
});
const StripeTierTableResponse = types.model('StripeTierTableResponse', {
  tierPrice: types.optional(types.array(StripePriceTierItem), [])
});
const StripePriceListResponse = types.model('StripePriceListResponse', {
  id: types.optional(types.string, ''),
  priceName: types.optional(types.string, ''),
  priceType: types.optional(types.string, ''),
  fixedPrice: types.maybeNull(types.optional(types.number, 0)),
  minimumPrice: types.optional(types.maybeNull(types.number), null),
  tierTableResponse: types.maybeNull(types.optional(StripeTierTableResponse, {})),
  productName: types.optional(types.string, ''),
  productId: types.optional(types.string, ''),
  meterEvent: types.maybeNull(types.optional(types.string, ''))
});
const StripeCouponProduct = types.model('StripeCouponProduct', {
  id: types.optional(types.string, ''),
  /** Null on the wire once the product is gone; the UI labels it instead of dropping it. */
  name: types.optional(types.string, ''),
  isDeleted: types.optional(types.boolean, false)
});
const StripeCouponRedemptionResponse = types.model('StripeCouponRedemptionResponse', {
  id: types.optional(types.string, ''),
  couponId: types.optional(types.string, ''),
  couponName: types.optional(types.maybeNull(types.string), ''),
  code: types.optional(types.maybeNull(types.string), ''),
  discountType: types.optional(types.string, ''),
  discountValue: types.optional(types.number, 0),
  companyId: types.optional(types.string, ''),
  /** Null when the coupon was applied across the whole account. */
  propertyId: types.optional(types.maybeNull(types.string), null),
  redeemedOn: types.optional(types.maybeNull(types.string), null),
  expiresOn: types.optional(types.maybeNull(types.string), null),
  isExpired: types.optional(types.boolean, false)
});

/** One redemption of a coupon, named for the admin review of how that coupon is used. */
const StripeCouponRedemptionDetailResponse = types.model('StripeCouponRedemptionDetailResponse', {
  id: types.optional(types.string, ''),
  companyId: types.optional(types.string, ''),
  companyName: types.optional(types.string, ''),
  propertyId: types.optional(types.maybeNull(types.string), null),
  propertyAddress: types.optional(types.maybeNull(types.string), null),
  redeemedOn: types.optional(types.maybeNull(types.string), null),
  expiresOn: types.optional(types.maybeNull(types.string), null),
  isExpired: types.optional(types.boolean, false)
});
const StripeCouponResponse = types.model('StripeCouponResponse', {
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  code: types.optional(types.string, ''),
  /** Backend stores Draft or Active only; Expired and Depleted are derived in the UI. */
  status: types.optional(types.string, ''),
  discountType: types.optional(types.string, ''),
  discountValue: types.optional(types.number, 0),
  expirationDate: types.optional(types.maybeNull(types.string), null),
  durationMonths: types.optional(types.maybeNull(types.number), null),
  maxRedemptions: types.optional(types.maybeNull(types.number), null),
  redemptionsCount: types.optional(types.number, 0),
  scope: types.optional(types.string, ''),
  /** Empty means the coupon discounts the entire invoice. */
  appliesToProducts: types.optional(types.array(StripeCouponProduct), [])
});
const StripeMeterResponse = types.model('StripeMeterResponse', {
  name: types.optional(types.maybeNull(types.string), ''),
  value: types.optional(types.maybeNull(types.number), 0)
});
const StripeSubscriptionTemplateProduct = types.model('StripeSubscriptionTemplateProduct', {
  productId: types.optional(types.string, ''),
  productName: types.optional(types.string, ''),
  defaultPriceId: types.optional(types.maybeNull(types.string), null)
});
const StripeSubscriptionTemplate = types.model('StripeSubscriptionTemplate', {
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  applicableCompanyProductType: types.optional(types.string, ''),
  products: types.optional(types.array(StripeSubscriptionTemplateProduct), [])
});
const SubscriptionCompanyItem = types.model('SubscriptionCompanyItem', {
  priceId: types.maybeNull(types.string),
  productId: types.maybeNull(types.string),
  productName: types.maybeNull(types.string),
  priceType: types.maybeNull(types.string),
  meterEvent: types.maybeNull(types.string),
  fixedPrice: types.maybeNull(types.number),
  currentQuantity: types.maybeNull(types.number),
  currentUnitPrice: types.maybeNull(types.number),
  currentTotal: types.maybeNull(types.number)
});
const SubscriptionCompanySubscription = types.model('SubscriptionCompanySubscription', {
  id: types.maybeNull(types.string),
  companyId: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  scope: types.maybeNull(types.string),
  startDate: types.maybeNull(types.string),
  term: types.maybeNull(types.string),
  commitmentEndDate: types.maybeNull(types.string),
  renewalDate: types.maybeNull(types.string),
  propertyIds: types.optional(types.array(types.string), []),
  items: types.optional(types.array(SubscriptionCompanyItem), [])
});
const SubscriptionCompanyDiscount = types.model('SubscriptionCompanyDiscount', {
  code: types.optional(types.maybeNull(types.string), ''),
  description: types.optional(types.maybeNull(types.string), ''),
  amount: types.optional(types.number, 0)
});
const SubscriptionCompanyPaymentSummary = types.model('SubscriptionCompanyPaymentSummary', {
  nextInvoiceDate: types.maybeNull(types.string),
  /** What the charges come to before coupons. */
  subtotalAmount: types.optional(types.number, 0),
  discountAmount: types.optional(types.number, 0),
  discounts: types.optional(types.array(SubscriptionCompanyDiscount), []),
  /** Already discounted — this is what the customer will be charged. */
  nextInvoiceAmount: types.maybeNull(types.number)
});
const SubscriptionCompanyData = types.model('SubscriptionCompanyData', {
  companyId: types.maybeNull(types.string),
  subscriptions: types.optional(types.array(SubscriptionCompanySubscription), []),
  paymentSummary: types.optional(SubscriptionCompanyPaymentSummary, {})
});
const StripeStore = types.model('StripeStore', {
  getPricesAsync,
  getMeterAsync,
  getInvoicesAsync,
  createPriceAsync,
  deletePriceAsync,
  updatePriceAsync,
  createProductAsync,
  deleteProductAsync,
  updateProductAsync,
  getAllProductsAsync,
  getProductPlansAsync,
  setProductPlanAsync,
  getCouponsAsync,
  createCouponAsync,
  updateCouponAsync,
  deleteCouponAsync,
  getCompanyCouponRedemptionsAsync,
  getCouponRedemptionsAsync,
  applyCouponAsync,
  revokeCouponRedemptionAsync,
  getOwnCompanyCouponRedemptionsAsync,
  redeemCouponAsync,
  bulkDeletePricesAsync,
  getPriceDashboardAsync,
  getSubscriptionTemplatesAsync,
  createSubscriptionAsync,
  updateSubscriptionAsync,
  getUpcomingInvoicesAsync,
  getSubscriptionsPagedAsync,
  getSubscriptionsLoadAllAsync,
  bulkUpdateSubscriptionsAsync,
  detachPriceFromSubscriptionAsync,
  getSubscriptionCompanyAsync,
  getSubscriptionCompanyForCompanyAsync,
  getScheduledChangeEstimateAsync,
  scheduleChangeAsync,
  rescheduleChangeAsync,
  removeScheduledChangeAsync,
  // getSubscriptionDashboardAsync,
  productList: types.optional(types.array(StripePrice), []),
  // Each product's plan by product id, from GET product/plans; read only while billing options are on.
  productPlans: types.optional(types.frozen(), {}),
  // Whether a plan read has succeeded: until one has, a product's plan is unknown, not "none".
  productPlansLoaded: types.optional(types.boolean, false),
  priceDashboard: types.optional(types.array(StripeProductWithPricesResponse), []),
  subscriptionDashboard: types.optional(types.array(StripeSubscriptionDashboardResponse), []),
  subscriptionsPaged: types.optional(types.array(StripeSubscriptionDashboardResponse), []),
  subscriptionsPagedTotal: types.optional(types.number, 0),
  subscriptionsListLoaded: types.optional(types.boolean, false),
  prices: types.optional(types.array(StripePriceListResponse), []),
  coupons: types.optional(types.array(StripeCouponResponse), []),
  couponsListLoaded: types.optional(types.boolean, false),
  companyRedemptions: types.optional(types.array(StripeCouponRedemptionResponse), []),
  couponRedemptions: types.optional(types.array(StripeCouponRedemptionDetailResponse), []),
  productDeleteErrorMessage: types.optional(types.maybeNull(types.string), null),
  meters: types.optional(types.array(StripeMeterResponse), []),
  subscriptionTemplates: types.optional(types.array(StripeSubscriptionTemplate), []),
  subscriptionCompanyData: types.optional(SubscriptionCompanyData, {})
}).actions(self => ({
  noteProductPlan(_ref) {
    var _self$productPlans$id, _self$productPlans;
    let id = _ref.id,
      plan = _ref.plan,
      addOnNeedsPlan = _ref.addOnNeedsPlan;
    const saved = (_self$productPlans$id = (_self$productPlans = self.productPlans) === null || _self$productPlans === void 0 ? void 0 : _self$productPlans[id]) !== null && _self$productPlans$id !== void 0 ? _self$productPlans$id : {
      productId: id,
      activeCompanies: 0
    };
    self.productPlans = _objectSpread(_objectSpread({}, self.productPlans), {}, {
      [id]: _objectSpread(_objectSpread({}, saved), {}, {
        plan,
        addOnNeedsPlan
      })
    });
  },
  setProductPlans(data) {
    const list = Array.isArray(data) ? data : [];
    self.productPlans = Object.fromEntries(list.map(row => [row.productId, row]));
    self.productPlansLoaded = true;
  },
  setMeters(data) {
    try {
      self.meters = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setPrices(data) {
    try {
      self.prices = cast([]);
      const list = Array.isArray(data) ? data : [];
      const normalized = list.map(row => _objectSpread(_objectSpread({}, row), {}, {
        minimumPrice: resolveMinimumPrice(row),
        tierTableResponse: normalizeTierTableSnapshotForPriceList(row.tierTableResponse)
      }));
      self.prices = cast(normalized);
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setCoupons(data) {
    try {
      const list = Array.isArray(data) ? data : [];
      const normalized = list.map(row => _objectSpread(_objectSpread({}, row), {}, {
        appliesToProducts: Array.isArray(row.appliesToProducts) ? row.appliesToProducts.map(product => {
          var _product$id;
          return {
            id: String((_product$id = product === null || product === void 0 ? void 0 : product.id) !== null && _product$id !== void 0 ? _product$id : ''),
            name: (product === null || product === void 0 ? void 0 : product.name) == null ? '' : String(product.name),
            isDeleted: !!(product !== null && product !== void 0 && product.isDeleted)
          };
        }) : []
      }));
      self.coupons = cast(normalized);
      self.couponsListLoaded = true;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setProductList(data) {
    try {
      self.productList = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setPriceDashboard(data) {
    try {
      const normalized = (data !== null && data !== void 0 ? data : []).map(product => {
        var _product$priceRespons;
        return _objectSpread(_objectSpread({}, product), {}, {
          priceResponse: ((_product$priceRespons = product.priceResponse) !== null && _product$priceRespons !== void 0 ? _product$priceRespons : []).map(p => {
            var _ref2, _p$priceId, _ref3, _p$name, _ref4, _p$type, _ref5, _p$isDefault, _p$subscriptionCount;
            const row = p;
            const meterEvent = p.meterEvent != null && typeof p.meterEvent === 'string' ? p.meterEvent : null;
            return _objectSpread(_objectSpread({}, p), {}, {
              priceId: (_ref2 = (_p$priceId = p.priceId) !== null && _p$priceId !== void 0 ? _p$priceId : p.id) !== null && _ref2 !== void 0 ? _ref2 : '',
              name: (_ref3 = (_p$name = p.name) !== null && _p$name !== void 0 ? _p$name : p.priceName) !== null && _ref3 !== void 0 ? _ref3 : '',
              type: (_ref4 = (_p$type = p.type) !== null && _p$type !== void 0 ? _p$type : p.priceType) !== null && _ref4 !== void 0 ? _ref4 : '',
              isDefault: (_ref5 = (_p$isDefault = p.isDefault) !== null && _p$isDefault !== void 0 ? _p$isDefault : p.default) !== null && _ref5 !== void 0 ? _ref5 : false,
              meterEvent,
              minimumPrice: resolveMinimumPrice(row),
              subscriptionCount: (_p$subscriptionCount = p.subscriptionCount) !== null && _p$subscriptionCount !== void 0 ? _p$subscriptionCount : Array.isArray(p.subscriptionResponse) ? p.subscriptionResponse.length : 0
            });
          })
        });
      });
      self.priceDashboard = normalized;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setSubscriptionDashboard(data) {
    try {
      self.subscriptionDashboard = data;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setSubscriptionsPaged(items, totalCount) {
    try {
      const rawPricesKey = item => {
        var _ref6, _ref7, _item$prices;
        return (_ref6 = (_ref7 = (_item$prices = item === null || item === void 0 ? void 0 : item.prices) !== null && _item$prices !== void 0 ? _item$prices : item === null || item === void 0 ? void 0 : item.pricings) !== null && _ref7 !== void 0 ? _ref7 : item === null || item === void 0 ? void 0 : item.priceResponse) !== null && _ref6 !== void 0 ? _ref6 : item === null || item === void 0 ? void 0 : item.priceList;
      };
      const normalized = (items !== null && items !== void 0 ? items : []).map(item => {
        const rawPrices = rawPricesKey(item);
        const prices = Array.isArray(rawPrices) ? rawPrices.map(p => {
          var _ref8, _ref9, _p$id;
          return _objectSpread(_objectSpread({}, p), {}, {
            id: (_ref8 = (_ref9 = (_p$id = p === null || p === void 0 ? void 0 : p.id) !== null && _p$id !== void 0 ? _p$id : p === null || p === void 0 ? void 0 : p.priceId) !== null && _ref9 !== void 0 ? _ref9 : p === null || p === void 0 ? void 0 : p.code) !== null && _ref8 !== void 0 ? _ref8 : '',
            meterEvent: normalizeMeterEvent(p === null || p === void 0 ? void 0 : p.meterEvent),
            minimumPrice: resolveMinimumPrice(p)
          });
        }) : [];
        const raw = item;
        const activeUnitsCount = typeof raw.activeUnitsCount === 'number' ? raw.activeUnitsCount : 0;
        const billableUnitsCount = typeof raw.billableUnitsCount === 'number' ? raw.billableUnitsCount : 0;
        return _objectSpread(_objectSpread({}, item), {}, {
          subscriptionPropertyIds: Array.isArray(item === null || item === void 0 ? void 0 : item.subscriptionPropertyIds) ? item.subscriptionPropertyIds : [],
          prices,
          activeUnitsCount,
          billableUnitsCount
        });
      });
      self.subscriptionsPaged = cast(normalized);
      self.subscriptionsPagedTotal = typeof totalCount === 'number' ? totalCount : 0;
      self.subscriptionsListLoaded = true;
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  resetSubscriptionsListLoaded() {
    self.subscriptionsListLoaded = false;
  },
  setCompanyRedemptions(data) {
    try {
      const list = Array.isArray(data) ? data : [];
      // couponName and code arrive null when the coupon row is gone; the models take null but
      // the UI reads them as strings, so normalise here rather than at every call site.
      const normalized = list.map(row => _objectSpread(_objectSpread({}, row), {}, {
        couponName: (row === null || row === void 0 ? void 0 : row.couponName) == null ? '' : String(row.couponName),
        code: (row === null || row === void 0 ? void 0 : row.code) == null ? '' : String(row.code)
      }));
      self.companyRedemptions = cast(normalized);
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  clearCompanyRedemptions() {
    self.companyRedemptions = cast([]);
  },
  setCouponRedemptions(data) {
    try {
      const list = Array.isArray(data) ? data : [];
      // The name is blank when the company has no settings row; the UI shows the id instead,
      // so keep it a string rather than letting a null through.
      const normalized = list.map(row => _objectSpread(_objectSpread({}, row), {}, {
        companyName: (row === null || row === void 0 ? void 0 : row.companyName) == null ? '' : String(row.companyName)
      }));
      self.couponRedemptions = cast(normalized);
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  clearCouponRedemptions() {
    self.couponRedemptions = cast([]);
  },
  resetCouponsListLoaded() {
    self.couponsListLoaded = false;
  },
  setProductDeleteErrorMessage(message) {
    self.productDeleteErrorMessage = message !== null && message !== void 0 ? message : null;
  },
  setSubscriptionTemplates(data) {
    try {
      self.subscriptionTemplates = cast(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setSubscriptionCompany(data) {
    try {
      self.subscriptionCompanyData = cast(data);
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  }
})).views(self => ({
  get couponsList() {
    return toJS(self.coupons);
  },
  get companyRedemptionsList() {
    return toJS(self.companyRedemptions);
  },
  get couponRedemptionsList() {
    return toJS(self.couponRedemptions);
  },
  get subscriptionCompany() {
    return toJS(self.subscriptionCompanyData);
  },
  get subscriptionCompanySubscriptions() {
    return toJS(self.subscriptionCompanyData.subscriptions);
  },
  get subscriptionCompanyPaymentSummary() {
    return toJS(self.subscriptionCompanyData.paymentSummary);
  }
}));
export const stripe = types.optional(StripeStore, {});